import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import ConfirmRecievedInvitationModal from "./ConfirmRecievedInvitationModal";
import DeclineRecievedInvitationModal from "./DeclineRecievedInvitationModal";
import {
  convertUTCToDate,
  convertUTCToDateInvitation,
  convertUTCToTime12Hour,
} from "../../../Utils/dateUtils";
import SpecialInstructionsDisplay from "./SpecialInstructionsDisplay";
import { isFuneralEvent, FuneralHostLine } from "./funeralFlyer";

const formatFullName = (member) =>
  [member?.first_name, member?.last_name].filter(Boolean).join(" ");

const RSVP_CLOSED_MESSAGE =
  "RSVPs are no longer accepted for this event. Please contact the host privately by phone or text outside of this app.";

const RecieveInvitation = ({
  search,
  onCountChange,
  events,
  loading,
  onRefresh,
  onFilterChange,
  receivedEventsCounts = { all: 0, accepted: 0, declined: 0, pending: 0 },
}) => {
  const navigate = useNavigate();
  const [filteredTab, setFilteredTab] = useState("all");
  const [invitations, setInvitations] = useState([]);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  // Update invitations when events prop changes
  // Update invitations when events prop changes
  useEffect(() => {
    setInvitations(events || []);
  }, [events]);

  const handleTabClick = (tab) => {
    setFilteredTab(tab);

    // Call the API fetcher (onFilterChange = fetchEvents from parent)
    // The parent will call /get_events?type=1&filter={tab}
    if (onFilterChange) {
      onFilterChange(tab);
    }
  };
  /// ===== Filter Logic =====
  // Search is already applied by the API (parent passes search to get_events).
  // Do not filter by title only here, or events that matched by host name (e.g. "Sanket Sinha") would be hidden.
  const filteredInvitations = invitations;

  // Notify parent whenever count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(filteredInvitations.length);
    }
  }, [filteredInvitations, onCountChange]);

  // ===== Handle Modal Open =====
  const openAcceptModal = (inv) => {
    setSelectedInvitation(inv);
    setShowAcceptModal(true);
  };

  const openDeclineModal = (inv) => {
    setSelectedInvitation(inv);
    setShowDeclineModal(true);
  };

  /** True when today's calendar date (UTC, matching invitation dates) is after the RSVP deadline date. */
  const isPastRsvpDeadline = (inv) => {
    const rsvpMs = inv?.Rsvp_deadline;
    if (!rsvpMs) return false;
    const deadlineStr = convertUTCToDate(rsvpMs);
    const todayStr = convertUTCToDate(Date.now());
    if (!deadlineStr || !todayStr) return false;
    return todayStr > deadlineStr;
  };

  // Helper to check if we are inside the 3‑day window before the event date
  const isWithinThreeDaysOfEvent = (inv) => {
    const eventMs = Number(inv?.start_date || inv?.date);
    // If there's no valid event date, never hide the buttons based on this rule
    if (!eventMs || isNaN(eventMs)) return false;

    const eventStart = new Date(eventMs);
    eventStart.setUTCHours(0, 0, 0, 0);

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysUntilEvent =
      (eventStart.getTime() - today.getTime()) / msPerDay;

    return daysUntilEvent < 3;
  };

  // Pending = 0, Accepted = 1, Declined = 2
  const status = (inv) => {
    const s = inv?.invitation_status;
    if (s === 1 || s === "1") return "accepted";
    if (s === 2 || s === "2") return "declined";
    return "pending";
  };

  // Business rules:
  // - Pending: user can Accept/Decline anytime, even on the last date
  // - Accepted: user cannot Decline within 3 days of the event
  // - Declined: user cannot Accept within 3 days of the event
  const canDecline = (inv) => {
    const st = status(inv);
    if (st === "pending") return true;
    if (st === "accepted") return !isWithinThreeDaysOfEvent(inv);
    return false;
  };

  const canAccept = (inv) => {
    const st = status(inv);
    if (st === "pending") return true;
    if (st === "declined") return !isWithinThreeDaysOfEvent(inv);
    return false;
  };

  const getRsvpLockedMessage = (inv) => {
    if (!isWithinThreeDaysOfEvent(inv)) return "";
    const st = status(inv);
    if (st === "accepted") {
      return "You can't decline this invitation because changes are only allowed up to 3 days before the event date.";
    }
    if (st === "declined") {
      return "You can't accept this invitation because changes are only allowed up to 3 days before the event date.";
    }
    return "";
  };

  const showDeclineButton = (inv) => {
    const st = status(inv);
    if (st === "pending" && isPastRsvpDeadline(inv)) return false;
    if (!canDecline(inv)) return false;
    if (filteredTab === "accepted") return true;
    if (filteredTab === "declined") return false;
    if (filteredTab === "pending") return true;
    return st === "pending" || st === "accepted";
  };

  const showAcceptButton = (inv) => {
    const st = status(inv);
    if (st === "pending" && isPastRsvpDeadline(inv)) return false;
    if (!canAccept(inv)) return false;
    if (filteredTab === "accepted") return false;
    if (filteredTab === "declined") return true;
    if (filteredTab === "pending") return true;
    return st === "pending" || st === "declined";
  };

  // Loading state
  if (loading) {
    return (
      <div className="events-content">
        <div className="text-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ======= Tab =========== */}
      <div className="left-controls">
        <div className="view-tabs">
          {["all", "accepted", "declined", "pending"].map((tab) => {
            const count = 
              tab === "all" ? receivedEventsCounts.all :
              tab === "accepted" ? receivedEventsCounts.accepted :
              tab === "declined" ? receivedEventsCounts.declined :
              receivedEventsCounts.pending;
            return (
              <button
                key={tab}
                className={` ${filteredTab === tab ? "active" : ""}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* ========= Invitation List =============== */}

      <div className="events-scroll-area">
      <div className="event-grid">
        {filteredInvitations.length === 0 ? (
          <div className="received-invitation-empty">
            <p>No invitation received so far</p>
          </div>
        ) : (
          filteredInvitations.map((inv) => (
            <div className="event-card event-card-received" key={inv.id}>
              <img
                src={
                  inv.image || window.getPublicUrl("/assets/images/card-5.svg")
                }
                className="event-img"
                alt={inv.title || "Event"}
              />
              <div className="card-body">
                <div className={`event-details-formatted${isFuneralEvent(inv) ? " funeral-flyer" : ""}`}>
                  {isFuneralEvent(inv) ? (
                    <FuneralHostLine event={inv} formatName={formatFullName} />
                  ) : (
                    <>
                      {(inv.Host || inv.host_name) && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Host:</span>
                          <span className="event-detail-value">
                            {inv.Host
                              ? formatFullName(inv.Host) || "-"
                              : inv.host_name}
                          </span>
                        </div>
                      )}
                      {inv.CoHost && (
                        <div className="event-detail-row">
                          <span className="event-detail-label">Co-Host:</span>
                          <span className="event-detail-value">
                            {formatFullName(inv.CoHost) || "-"}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="event-detail-row">
                    <span className="event-detail-label">Name of Event:</span>
                    <span className="event-detail-value">
                      {inv.title || "-"}
                    </span>
                  </div>

                  <div className="event-detail-row">
                    <span className="event-detail-label">Event Date:</span>
                    <span className="event-detail-value">
                      {convertUTCToDateInvitation(inv.start_date || inv.date)}
                      {inv?.end_date &&
                        ` - ${convertUTCToDateInvitation(inv.end_date)}`}
                    </span>
                  </div>

                  <div className="event-detail-row">
                    <span className="event-detail-label">Time:</span>
                    <span className="event-detail-value">
                      {convertUTCToTime12Hour(inv.event_time || inv.time) || "-"}
                    </span>
                  </div>

                  {inv.venue && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">Venue:</span>
                      <span className="event-detail-value">{inv.venue}</span>
                    </div>
                  )}

                  <div className="event-detail-row">
                    <span className="event-detail-label ">Location:</span>
                    <span className="event-detail-value location-value">
                      {inv.state && inv.location ? (
                        <>
                          <div>{inv.location}</div>
                          <div>{inv.state}</div>
                        </>
                      ) : (
                        inv.state || inv.location || "-"
                      )}
                    </span>
                  </div>

                  {inv.document && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">View Add'l Details:</span>
                      <span className="event-detail-value">
                        <a
                          href={inv.document}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1890ff", textDecoration: "underline", wordBreak: "break-all" }}
                        >
                          View PDF
                        </a>
                      </span>
                    </div>
                  )}

                  <SpecialInstructionsDisplay text={inv.description} />

                  {inv.Rsvp_deadline && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">RSVP Deadline:</span>
                      <span className="event-detail-value">
                        {isFuneralEvent(inv) && "RSVP by "}
                        {convertUTCToDateInvitation(inv.Rsvp_deadline)}
                      </span>
                    </div>
                  )}

                  {/* Accept / Decline directly above Invitation Status (received cards use in-flow status; see App.css) */}
                  {status(inv) === "pending" && isPastRsvpDeadline(inv) ? (
                    <div className="received-invitation-actions">
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          padding: "8px 10px",
                          display: "inline-block",
                          lineHeight: 1.45,
                        }}
                      >
                        {RSVP_CLOSED_MESSAGE}
                      </span>
                    </div>
                  ) : showDeclineButton(inv) || showAcceptButton(inv) ? (
                    <div className="received-invitation-actions">
                      {showAcceptButton(inv) && (
                        <button
                          className="btn btn-accept"
                          onClick={() => openAcceptModal(inv)}
                        >
                          Accept
                        </button>
                      )}
                      {showDeclineButton(inv) && (
                        <button
                          className="btn btn-decline"
                          onClick={() => openDeclineModal(inv)}
                        >
                          Decline
                        </button>
                      )}
                    </div>
                  ) : (
                    !!getRsvpLockedMessage(inv) && (
                      <div className="received-invitation-actions">
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            padding: "4px 8px",
                            display: "inline-block",
                          }}
                        >
                          {getRsvpLockedMessage(inv)}
                        </span>
                      </div>
                    )
                  )}

                  <div className="event-detail-row event-invitation-status-section">
                    <div className="event-invitation-status-title">
                      {isFuneralEvent(inv) ? "Notice" : "Invitation Status"}
                    </div>
                    <div className="event-invitation-status-counts">
                      {isFuneralEvent(inv) ? (
                        <>
                          <strong>{inv?.total_invited ?? 0}</strong> Notice(s) Sent
                        </>
                      ) : (
                        <>
                          <strong>{inv?.total_invited ?? 0}</strong> Invited<span className="event-invitation-status-divider">|</span><strong>{inv?.accepted_count ?? 0}</strong> Accepted<span className="event-invitation-status-divider">|</span><strong>{inv?.declined_count ?? 0}</strong> Declined<span className="event-invitation-status-divider">|</span><strong>{inv?.pending_count ?? 0}</strong> Pending
                        </>
                      )}
                    </div>
                  </div>

                  {inv.status === 2 && (
                    <p
                      className="event-cancelled-label"
                      style={{
                        color: "#dc3545",
                        fontWeight: "bold",
                        marginTop: "6px",
                        marginBottom: "4px",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      Cancelled Event
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      </div>

      {/* <div>
        {filteredInvitations.length === 0 ? (
          <div>
            <p>No invitation found</p>
          </div>
        ) : (
          filteredInvitations.map((inv) => (
            <div key={inv.title}>
              <img src={inv.banner} alt="banner"/>

              <div>
                <div>
                  <h3>{inv.title}</h3>
                  <span>{inv.time}</span>
                </div>
                <p>
                  <strong>Location:</strong> {inv.location}
                </p>
                <p>
                  <strong>Host:</strong> {inv.host}
                </p>
                <p>{inv.desc}</p>
              </div>

              {inv.status === "pending" && (
                <div>
                  <button onClick={() => openAcceptModal(inv)}>
                    Accept
                  </button>
                  <button onClick={() => openDeclineModal(inv)}>
                    Decline
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div> */}

      {/* ===== ACCEPT MODAL ===== */}
      <Modal
        isOpen={showAcceptModal}
        onRequestClose={() => setShowAcceptModal(false)}
        ariaHideApp={false}
        contentLabel="Accept Invitation"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="modal-cs modal-recive"
      >
        <ConfirmRecievedInvitationModal
          invitation={selectedInvitation}
          onClose={(success) => {
            setShowAcceptModal(false);
            if (success && onRefresh) {
              onRefresh();
            }
          }}
        />
      </Modal>

      {/* ===== DECLINE MODAL ===== */}
      <Modal
        isOpen={showDeclineModal}
        onRequestClose={() => setShowDeclineModal(false)}
        ariaHideApp={false}
        contentLabel="Decline Invitation"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="modal-cs modal-recive"
      >
        <DeclineRecievedInvitationModal
          invitation={selectedInvitation}
          onClose={(success) => {
            setShowDeclineModal(false);
            if (success && onRefresh) {
              onRefresh();
            }
          }}
        />
      </Modal>
    </>
  );
};

export default RecieveInvitation;
